import React, { useState } from 'react';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import { RefreshCw, Cloud, AlertCircle, Check, Download, Loader2, ChevronDown, ChevronUp, Eye } from 'lucide-react';

interface GHLManagementProps {
    contacts: Contact[];
    onContactsUpdate: () => void;
}

interface SyncResult {
    success: boolean;
    message: string;
    error?: string;
    totalContacts?: number;
    createdCount?: number;
    updatedCount?: number;
    errorCount?: number;
    totalCustomFields?: number;
    contacts?: Contact[]; // Added to show the actual contacts
}

export default function GHLManagement({ contacts, onContactsUpdate }: GHLManagementProps) {
    const [pulling, setPulling] = useState(false);
    const [syncResults, setSyncResults] = useState<SyncResult | null>(null);
    const [showResults, setShowResults] = useState(false);
    const [expandedContact, setExpandedContact] = useState<string | null>(null);

    // Simple function to call the Supabase function directly
    const handlePullContacts = async () => {
        setPulling(true);
        setSyncResults(null);

        try {
            console.log('🚀 Pulling 10 contacts with ALL 91+ custom fields...');

            // Call the Supabase function using the client
            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {}
            });

            if (error) {
                throw new Error(error.message || 'Function call failed');
            }

            const result = data || {};

            // DEBUG: Log the full response to console
            console.log('🔍 Full Supabase function response:', result);
            console.log('📊 Contacts array:', result.contacts);
            console.log('📈 Contacts length:', result.contacts?.length || 0);

            setSyncResults(result);
            setShowResults(true);

            // Refresh contacts list to show new/updated contacts
            if (result.success) {
                onContactsUpdate();
            }
        } catch (error) {
            console.error('Pull contacts error:', error);
            setSyncResults({
                success: false,
                message: 'Failed to pull contacts from GoHighLevel',
                error: error instanceof Error ? error.message : 'Unknown error'
            });
            setShowResults(true);
        } finally {
            setPulling(false);
        }
    };

    const toggleContactExpansion = (contactId: string) => {
        setExpandedContact(expandedContact === contactId ? null : contactId);
    };

    const formatCustomFields = (customFieldsJson: string | null) => {
        if (!customFieldsJson) return [];
        try {
            return JSON.parse(customFieldsJson);
        } catch {
            return [];
        }
    };

    return (
        <div className="bg-black border border-gray-800 p-6 lg:p-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h3 className="text-2xl lg:text-3xl font-light text-white tracking-wide flex items-center space-x-3">
                        <div className="p-2 bg-gray-800 border border-gray-700">
                            <Cloud className="w-6 h-6 text-white" />
                        </div>
                        <span>GoHighLevel Integration</span>
                    </h3>
                    <p className="text-gray-300 text-base lg:text-lg mt-2 font-light">
                        Sync ALL contacts with their complete custom field data from GoHighLevel
                    </p>
                </div>
            </div>

            {/* Main Action Button */}
            <div className="flex justify-center mb-8">
                <button
                    onClick={handlePullContacts}
                    disabled={pulling}
                    className="inline-flex items-center justify-center px-12 py-5 bg-white text-black text-xl font-semibold hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 space-x-4 tracking-wide"
                >
                    {pulling ? (
                        <Loader2 className="w-7 h-7 animate-spin" />
                    ) : (
                        <Download className="w-7 h-7" />
                    )}
                    <span>
                        {pulling ? 'Syncing All Contacts...' : 'Sync All Contacts (Complete Data)'}
                    </span>
                </button>
            </div>

            {/* Results Display */}
            {showResults && syncResults && (
                <div className={`border-2 p-8 mb-8 ${syncResults.success
                    ? 'bg-gray-900 border-green-600'
                    : 'bg-gray-900 border-red-600'
                    }`}>
                    <div className="flex items-start space-x-4">
                        <div className={`p-2 ${syncResults.success ? 'bg-green-900' : 'bg-red-900'}`}>
                            {syncResults.success ? (
                                <Check className="w-7 h-7 text-green-400" />
                            ) : (
                                <AlertCircle className="w-7 h-7 text-red-400" />
                            )}
                        </div>
                        <div className="flex-1">
                            <p className={`font-bold text-xl ${syncResults.success ? 'text-green-400' : 'text-red-400'
                                }`}>
                                {syncResults.message}
                            </p>

                            {syncResults.success && syncResults.totalCustomFields && (
                                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <div className="text-center bg-black border border-gray-700 p-4">
                                        <p className="text-3xl font-light text-green-400">{syncResults.totalContacts || 0}</p>
                                        <p className="text-sm font-medium text-gray-400 mt-1 tracking-wide uppercase">Contacts</p>
                                    </div>
                                    <div className="text-center bg-black border border-gray-700 p-4">
                                        <p className="text-3xl font-light text-blue-400">{syncResults.totalCustomFields}</p>
                                        <p className="text-sm font-medium text-gray-400 mt-1 tracking-wide uppercase">Custom Fields</p>
                                    </div>
                                    <div className="text-center bg-black border border-gray-700 p-4">
                                        <p className="text-3xl font-light text-purple-400">{syncResults.createdCount || 0}</p>
                                        <p className="text-sm font-medium text-gray-400 mt-1 tracking-wide uppercase">Created</p>
                                    </div>
                                    <div className="text-center bg-black border border-gray-700 p-4">
                                        <p className="text-3xl font-light text-orange-400">{syncResults.updatedCount || 0}</p>
                                        <p className="text-sm font-medium text-gray-400 mt-1 tracking-wide uppercase">Updated</p>
                                    </div>
                                </div>
                            )}

                            {syncResults.error && (
                                <p className="text-sm text-red-400 mt-2">{syncResults.error}</p>
                            )}
                        </div>
                        <button
                            onClick={() => setShowResults(false)}
                            className="text-gray-400 hover:text-white text-xl"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* DEBUG: Show conditions for contact details display */}
            {showResults && syncResults && (
                <div className="mb-4 p-3 bg-gray-900 border border-gray-700 text-xs text-gray-300">
                    <p><strong>Debug Info:</strong></p>
                    <p>showResults: {showResults.toString()}</p>
                    <p>syncResults.success: {syncResults.success?.toString()}</p>
                    <p>syncResults.contacts exists: {(!!syncResults.contacts).toString()}</p>
                    <p>syncResults.contacts length: {syncResults.contacts?.length || 0}</p>
                    <p>Should show contact details: {(showResults && syncResults?.success && syncResults.contacts && syncResults.contacts.length > 0).toString()}</p>
                </div>
            )}

            {/* Contact Details Display */}
            {showResults && syncResults?.success && syncResults.contacts && syncResults.contacts.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-lg font-light text-white mb-4 flex items-center space-x-2 tracking-wide">
                        <Eye className="w-5 h-5 text-gray-400" />
                        <span>Contacts Added/Updated (Click to inspect fields)</span>
                    </h4>
                    <div className="space-y-3">
                        {syncResults.contacts.map((contact) => {
                            const isExpanded = expandedContact === contact.id;
                            const customFields = formatCustomFields(contact.custom_fields_json);

                            return (
                                <div key={contact.id} className="border border-gray-700 bg-gray-900">
                                    <button
                                        onClick={() => toggleContactExpansion(contact.id)}
                                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-800 transition-colors text-left"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="text-left">
                                                <p className="font-medium text-white">
                                                    {contact.first_name} {contact.last_name}
                                                </p>
                                                <p className="text-sm text-gray-400">
                                                    {contact.email} • {contact.phone} • {customFields.length} custom fields
                                                </p>
                                            </div>
                                        </div>
                                        {isExpanded ? (
                                            <ChevronUp className="w-5 h-5 text-gray-400" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                        )}
                                    </button>

                                    {isExpanded && (
                                        <div className="px-4 pb-4 border-t border-gray-700">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                                {/* Core Fields */}
                                                <div>
                                                    <h5 className="font-medium text-white mb-2">Core Fields</h5>
                                                    <div className="space-y-1 text-sm text-gray-300">
                                                        <p><span className="font-medium text-gray-400">Name:</span> {contact.first_name} {contact.last_name}</p>
                                                        <p><span className="font-medium text-gray-400">Email:</span> {contact.email || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">Phone:</span> {contact.phone || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">Company:</span> {contact.company || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">Title:</span> {contact.title || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">Address:</span> {contact.address || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">City:</span> {contact.city || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">State:</span> {contact.state || 'N/A'}</p>
                                                        <p><span className="font-medium text-gray-400">GHL ID:</span> {contact.ghl_contact_id}</p>
                                                    </div>
                                                </div>

                                                {/* Custom Fields */}
                                                <div>
                                                    <h5 className="font-medium text-white mb-2">
                                                        Custom Fields ({customFields.length} total)
                                                    </h5>
                                                    <div className="max-h-60 overflow-y-auto space-y-1 text-sm">
                                                        {customFields.length > 0 ? (
                                                            customFields.map((field: any, index: number) => (
                                                                <div key={index} className="p-2 bg-gray-800 border border-gray-700">
                                                                    <p className="font-medium text-gray-300">
                                                                        {field.id || field.key || `Field ${index + 1}`}
                                                                    </p>
                                                                    <p className="text-gray-400 break-words">
                                                                        {field.value || 'No value'}
                                                                    </p>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p className="text-gray-500 italic">No custom fields found</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* First 5 Custom Fields (Specific Columns) */}
                                            {(contact.custom_field_1_label || contact.custom_field_2_label ||
                                                contact.custom_field_3_label || contact.custom_field_4_label ||
                                                contact.custom_field_5_label) && (
                                                    <div className="mt-4 pt-4 border-t border-gray-700">
                                                        <h5 className="font-medium text-white mb-2">First 5 Custom Fields (Specific Columns)</h5>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-300">
                                                            {contact.custom_field_1_label && (
                                                                <p><span className="font-medium text-gray-400">{contact.custom_field_1_label}:</span> {contact.custom_field_1_value}</p>
                                                            )}
                                                            {contact.custom_field_2_label && (
                                                                <p><span className="font-medium text-gray-400">{contact.custom_field_2_label}:</span> {contact.custom_field_2_value}</p>
                                                            )}
                                                            {contact.custom_field_3_label && (
                                                                <p><span className="font-medium text-gray-400">{contact.custom_field_3_label}:</span> {contact.custom_field_3_value}</p>
                                                            )}
                                                            {contact.custom_field_4_label && (
                                                                <p><span className="font-medium text-gray-400">{contact.custom_field_4_label}:</span> {contact.custom_field_4_value}</p>
                                                            )}
                                                            {contact.custom_field_5_label && (
                                                                <p><span className="font-medium text-gray-400">{contact.custom_field_5_label}:</span> {contact.custom_field_5_value}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Info Panel */}
            <div className="bg-gray-900 border border-gray-700 p-4">
                <h5 className="text-sm font-medium text-white mb-2 tracking-wide uppercase">What This Does:</h5>
                <ul className="text-sm text-gray-300 space-y-1 font-light">
                    <li>• Pulls exactly 10 contacts from GoHighLevel</li>
                    <li>• Fetches complete individual contact data (all 91+ custom fields)</li>
                    <li>• Rate limited to stay within API limits (~857ms between calls)</li>
                    <li>• Saves or updates contacts in your Vault Contact database</li>
                    <li>• Shows you the contacts and their fields for manual inspection</li>
                    <li>• Runs as a background function - you can navigate away during processing</li>
                </ul>
            </div>
        </div>
    );
}
